import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type {
  ContextWindowPrompt,
  ConversationDebugDTO,
  ConversationDebugMessageDTO,
  ConversationDebugToolCallDTO,
} from "@shaarp/common";
import { ChevronDownIcon } from "lucide-react";
import { DebugLoading, DebugQuote } from "./debug-tab-parts";
import { compactNumber, formatBounds, formatDate, fullNumber } from "./format";
import { isSearchBudgetSpent, readSearchTrace } from "./read-search-trace";
import { SearchTrace } from "./search-trace";

type MessagesTabProps = {
  debug: ConversationDebugDTO | undefined;
  isLoading: boolean;
};

/** Every turn of the conversation, dimmed once the model stopped reading it. */
export function MessagesTab({ debug, isLoading }: MessagesTabProps) {
  if (isLoading) return <DebugLoading />;
  if (!debug) return null;

  return (
    <div className="space-y-2">
      {debug.messages.map((message, index) => (
        <MessageCard key={message.id} message={message} position={index + 1} />
      ))}
    </div>
  );
}

/**
 * One turn, collapsed to its shape and expandable to everything it ran with.
 *
 * Collapsed is the index — role, model, weight, the words it opens and closes
 * on. Expanded is the turn itself: its text in full, what the model thought,
 * every tool call with what it was asked and what came back, and the prompt it
 * read. Reading that in the panel rather than through the clipboard is the
 * point: the report is for handing to an agent, this is for looking.
 */
function MessageCard({
  message,
  position,
}: {
  message: ConversationDebugMessageDTO;
  position: number;
}) {
  const { t } = useTranslation();

  return (
    <Collapsible
      className={cn(
        "group/message rounded-md border text-xs",
        message.isOutOfContext && "opacity-50"
      )}
    >
      <CollapsibleTrigger className="w-full space-y-1 p-2.5 text-left transition-colors hover:bg-muted/50">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-muted-foreground">#{position}</span>
          <Badge variant="outline">{message.role}</Badge>
          {message.type !== "REGULAR" && (
            <Badge variant="warning">{message.type}</Badge>
          )}
          {message.isOutOfContext && (
            <Badge variant="secondary">
              {t("chat.debug.messages.outOfContext")}
            </Badge>
          )}
          <span className="ml-auto font-mono text-muted-foreground">
            {formatDate(message.at)}
          </span>
          <ChevronDownIcon className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/message:rotate-180" />
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
          <span className="font-mono">{message.llmId}</span>
          <span>
            {t("chat.debug.messages.chars", {
              count: fullNumber(message.contentLength),
            })}
          </span>
          {message.contextUsage && (
            <span className="font-mono">
              {t("chat.debug.messages.tokens", {
                tokens: compactNumber(message.contextUsage.estimatedTokensUsed),
                tier: message.contextUsage.compactionTier,
              })}
            </span>
          )}
        </div>
        <DebugQuote
          className="italic"
          text={formatBounds(message.contentStart, message.contentEnd)}
        />
        <ToolCallBadges calls={message.toolCalls} />
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-3 border-t p-2.5">
        {message.reasoning.length > 0 && (
          <Block label={t("chat.debug.messages.reasoning")}>
            {message.reasoning.map((thought, index) => (
              <Pre key={index} text={thought} />
            ))}
          </Block>
        )}

        {message.toolCalls.map((call, index) => (
          <ToolCallDetail call={call} key={`${call.type}-${index}`} />
        ))}

        <Block label={t("chat.debug.messages.content")}>
          {message.content.trim().length === 0 ? (
            <Empty text={t("chat.debug.messages.noProse")} />
          ) : (
            <Pre text={message.content} />
          )}
        </Block>

        <PromptBlocks prompt={message.contextUsage?.prompt} />
      </CollapsibleContent>
    </Collapsible>
  );
}

/** A labelled section inside an expanded turn. */
function Block({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      {children}
    </div>
  );
}

/**
 * Raw text, wrapped and scrollable.
 *
 * `whitespace-pre-wrap` rather than a plain paragraph: a prompt is laid out in
 * lines and reading it reflowed into one block is how a missing section goes
 * unnoticed. The height cap keeps a 100 000-character system prompt from
 * burying every turn under it.
 */
function Pre({ text }: { text: string }) {
  return (
    <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 font-mono text-[11px] leading-relaxed">
      {text}
    </pre>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-muted-foreground italic">{text}</p>;
}

function ToolCallDetail({ call }: { call: ConversationDebugToolCallDTO }) {
  const { t } = useTranslation();
  const searchTrace = readSearchTrace(call.output);

  return (
    <Block label={`${call.type.replace(/^tool-/, "")} · ${call.state ?? "—"}`}>
      {searchTrace && (
        <>
          <p className="text-muted-foreground">
            {t("chat.debug.messages.searchTrace.title")}
          </p>
          <SearchTrace trace={searchTrace} />
        </>
      )}
      <p className="text-muted-foreground">
        {t("chat.debug.messages.toolInput")}
      </p>
      <Pre text={toReadableJson(call.input)} />
      <p className="text-muted-foreground">
        {t("chat.debug.messages.toolOutput")}
      </p>
      {call.output === undefined ? (
        // Not a failure: most outputs are dropped when the message is written,
        // long before this read. Said in words, because a blank box reads as a
        // tool that returned nothing.
        <Empty text={t("chat.debug.messages.outputNotPersisted")} />
      ) : (
        <>
          {isSearchBudgetSpent(call.output) && (
            <Badge variant="outline" className="w-fit">
              {t("chat.debug.messages.searchBudgetSpent")}
            </Badge>
          )}
          <Pre text={toReadableJson(call.output)} />
        </>
      )}
    </Block>
  );
}

function PromptBlocks({
  prompt,
}: {
  prompt: ContextWindowPrompt | undefined;
}) {
  const { t } = useTranslation();

  if (!prompt) {
    return (
      <Block label={t("chat.debug.messages.prompt")}>
        <Empty text={t("chat.debug.messages.noPrompt")} />
      </Block>
    );
  }

  const blocks: [string, string][] = [
    [t("chat.debug.messages.promptSystem"), prompt.system],
    [t("chat.debug.messages.promptMemory"), prompt.memory],
    [t("chat.debug.messages.promptKnowledge"), prompt.knowledge],
    [t("chat.debug.messages.promptTactical"), prompt.tactical],
  ];
  // Turns recorded before the context data message existed carry that content
  // inside `system`, and have no line of their own to show.
  if (prompt.context !== undefined) {
    blocks.push([t("chat.debug.messages.promptContext"), prompt.context]);
  }

  return (
    <Block label={t("chat.debug.messages.prompt")}>
      {blocks.map(([label, content]) => (
        <div className="space-y-1" key={label}>
          <p className="text-muted-foreground">
            {label} ·{" "}
            {t("chat.debug.messages.chars", { count: fullNumber(content.length) })}
          </p>
          {content.trim().length === 0 ? (
            <Empty text={t("chat.debug.messages.emptyBlock")} />
          ) : (
            <Pre text={content} />
          )}
        </div>
      ))}
    </Block>
  );
}

/** A persisted payload as readable JSON, never as `[object Object]`. */
function toReadableJson(value: unknown): string {
  if (value === undefined) return "—";
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    // A cycle cannot survive the database round-trip, but the panel must not
    // go blank on the one that does.
    return "—";
  }
}

function ToolCallBadges({ calls }: { calls: ConversationDebugToolCallDTO[] }) {
  if (calls.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {calls.map((call, index) => (
        <Badge
          key={`${call.type}-${index}`}
          variant={call.hasPersistedOutput ? "secondary" : "destructive"}
        >
          {call.type.replace(/^tool-/, "")}
          {call.state ? ` · ${call.state}` : ""}
        </Badge>
      ))}
    </div>
  );
}
